/**
 * Authentication Store
 *
 * Manages user authentication state, token expiration, and user information.
 * Uses cookie-based authentication with JWT tokens.
 *
 * State:
 * - user: Current logged-in user information
 * - isAuthenticated: Whether user is authenticated
 * - tokenExpiresAt: Unix timestamp when the access token expires
 * - isRefreshing: Whether a token refresh is in progress
 *
 * Key Features:
 * - Strict typing using @openbox/shared-types
 * - Token expiration tracking (stored in localStorage)
 * - Automatic cleanup on logout
 * - Error handling with state reset on 401
 *
 * Security Notes:
 * - Access tokens are stored in HTTP-only cookies (not accessible via JS)
 * - Only token expiration time is stored in localStorage
 * - Tokens are never exposed to JavaScript memory
 */

import type {
  AuthAuthGetSingleUserResponse,
  AuthAuthProcessLoginRequest,
  AuthAuthProcessLoginResponse,
} from '@openbox/shared-types/lib/auth/Auth'
import {
  SystemNaturalTypesEnum,
  SystemPersonTypesEnum,
} from '@openbox/shared-types/lib/system/system.enums'
import type {
  SystemSettingKeys,
  SystemSettingKeyTypeMap,
} from '@openbox/shared-types/lib/system/system.enums'
import type { FetchError } from 'ofetch'
import { defineStore } from 'pinia'
import type { AuthState, WorkspaceCompany } from '~/types/auth'
import {
  broadcastLogoutEvent,
  clearAuthStorage,
  decodeAccessToken,
  getRefreshingFlag,
  getTokenExpiration,
  saveTokenExpiration,
  setRefreshingFlag,
} from '~/utils/auth.utils'

// Default token expiration threshold for proactive refresh (5 minutes in milliseconds)
// Used as fallback when runtime config is not available (e.g., during SSR or testing)
export const DEFAULT_TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000

// Default number of decimal places for formatting money and numeric inputs
// Used as fallback when user settings are not available
export const DEFAULT_DECIMAL_PLACES = 2

/**
 * Get the token refresh buffer from runtime config or use default
 * This function safely handles cases where runtime config may not be available
 */
export function getTokenRefreshBuffer(): number {
  try {
    // useRuntimeConfig is auto-imported by Nuxt
    const config = useRuntimeConfig()
    return config.public.tokenRefreshBufferMs ?? DEFAULT_TOKEN_REFRESH_BUFFER_MS
  } catch {
    // Runtime config not available (e.g., during unit tests without Nuxt context)
    return DEFAULT_TOKEN_REFRESH_BUFFER_MS
  }
}

// Module-level Promise for single-flight token refresh within the same tab
// This ensures only one refresh request is in progress at a time
let refreshPromise: Promise<void> | null = null

/**
 * Reset the refresh promise
 * Used internally by clearState() and exported for test isolation before each test
 */
export function _resetRefreshPromise(): void {
  refreshPromise = null
}

export const useAuthStore = defineStore('auth', {
  /**
   * Initial state
   */
  state: (): AuthState => ({
    user: null,
    isAuthenticated: false,
    tokenExpiresAt: null,
    isRefreshing: false,
    sessionRestored: false,
    isSystemNavigation: false,
  }),

  /**
   * Computed getters
   */
  getters: {
    /**
     * Check if token is expiring soon (within the configured buffer time)
     * Uses milliseconds precision for consistent timing
     * Buffer time is configurable via NUXT_PUBLIC_TOKEN_REFRESH_BUFFER_MS env variable
     */
    isTokenExpiringSoon(): boolean {
      if (!this.tokenExpiresAt) return false
      const now = Date.now()
      return this.tokenExpiresAt - now <= getTokenRefreshBuffer()
    },

    /**
     * Check if token has expired
     * Uses milliseconds precision for consistent timing
     */
    isTokenExpired(): boolean {
      if (!this.tokenExpiresAt) return true
      const now = Date.now()
      return now >= this.tokenExpiresAt
    },

    /**
     * Get a user setting value by key from the user's workspace company settings
     * Returns the setting value or DEFAULT_DECIMAL_PLACES if not found
     *
     * @param key - The setting key from SystemSettingKeys enum
     * @returns The setting value with proper typing based on the key
     */
    getSetting(): <K extends SystemSettingKeys>(
      key: K
    ) => SystemSettingKeyTypeMap[K] {
      return <K extends SystemSettingKeys>(
        key: K
      ): SystemSettingKeyTypeMap[K] => {
        const settings = this.user?.workspace.company.settings
        if (settings && key in settings) {
          return settings[
            key as keyof typeof settings
          ] as SystemSettingKeyTypeMap[K]
        }
        // Return default value based on the key
        if (key === 'DECIMAL_PLACES') {
          return DEFAULT_DECIMAL_PLACES as SystemSettingKeyTypeMap[K]
        }
        return undefined as unknown as SystemSettingKeyTypeMap[K]
      }
    },

    /**
     * Spec 064 — la empresa del workspace puede sufrir retención de renta del 10%
     * sobre los servicios que factura: persona natural inscrita en IVA
     * (Art. 156 del Código Tributario).
     *
     * Único predicado del lado cliente; lo consumen el formulario de servicio,
     * su vista previa y el modal de línea de la venta. El servidor tiene el suyo
     * (`getContextCompany().isNaturalDeclaraIVA`) y es el que manda: éste solo
     * decide qué se ve.
     *
     * Encadenamiento opcional en toda la cadena: `companyType`/`naturalType` son
     * `| null` en el contrato (las jurídicas no tienen `naturalType`) y el getter
     * debe devolver `false` —nunca lanzar— cuando no hay usuario.
     */
    isNaturalDeclaraIVA(): boolean {
      const company = this.user?.workspace?.company

      return (
        company?.companyType?.id === SystemPersonTypesEnum.Natural
        && company?.naturalType?.id === SystemNaturalTypesEnum.DeclaraIVA
      )
    },

    /**
     * Get the list of companies and their branches the user has access to
     * Each company includes an active flag indicating if it's the current workspace company
     *
     * @returns Array of WorkspaceCompany objects with branches
     */
    companies(): WorkspaceCompany[] {
      if (!this.user?.profile.acceses) return []
      const currentCompanyId = this.user?.workspace?.company?.id
      return this.user.profile.acceses.map(access => ({
        companyId: access.id,
        companyName: access.name,
        branches: access.branches.map(b => ({ id: b.id, name: b.name })),
        active: access.id === currentCompanyId,
      }))
    },

    /**
     * Determines if the user can switch workspaces
     * Returns true if user has access to 2+ companies OR any company with 2+ branches
     *
     * @returns boolean indicating if workspace switching is available
     */
    canSwitchWorkspace(): boolean {
      const acceses = this.user?.profile.acceses
      if (!acceses) return false
      return acceses.length >= 2 || acceses.some(a => a.branches.length >= 2)
    },
  },

  /**
   * Actions
   */
  actions: {
    /**
     * Login with email and password
     *
     * @param credentials - Login credentials (email, password, remember)
     * @throws Error if login fails
     */
    async login(credentials: AuthAuthProcessLoginRequest): Promise<void> {
      const securedApi = useSecuredApi()

      try {
        // Call login endpoint
        const response = await securedApi<AuthAuthProcessLoginResponse>(
          '/auth/login',
          {
            method: 'POST',
            body: credentials,
          }
        )

        // Decode the access token to get expiration
        // Note: JWT exp claim is in seconds since epoch, not milliseconds
        const decoded = decodeAccessToken(response.data.access_token)

        if (!decoded) {
          throw new Error('Failed to decode access token')
        }

        // Store token expiration in state and localStorage
        const tokenExpiresAtMs = decoded.exp * 1000 // Convert to milliseconds
        this.tokenExpiresAt = tokenExpiresAtMs
        if (import.meta.client) {
          saveTokenExpiration(tokenExpiresAtMs)
        }

        // Fetch user information
        await this.fetchUser()
      } catch (error) {
        // Clear state on login failure
        this.clearState()
        throw error
      }
    },

    /**
     * Logout the current user
     *
     * Calls the backend logout endpoint and clears all state.
     * Always cleans up frontend state even if backend call fails.
     * Broadcasts a logout event to other tabs for cross-tab synchronization.
     */
    async logout(): Promise<void> {
      const securedApi = useSecuredApi()

      try {
        // Attempt to logout on backend
        await securedApi('/auth/logout', {
          method: 'POST',
        })
      } catch (error) {
        // Log error but continue with cleanup
        console.error('Logout endpoint failed:', error)
      } finally {
        // Broadcast logout event to other tabs before clearing state
        if (import.meta.client) {
          broadcastLogoutEvent()
        }
        // Always clear state regardless of backend response
        this.clearState()
      }
    },

    /**
     * Fetch current user information
     *
     * @param options.skipAuthRetry - When true, bypasses the auth interceptor's 401
     *   retry/toast logic. Used during session restoration where the caller handles errors.
     * @throws Error if user fetch fails (401 will clear state)
     */
    async fetchUser(options?: { skipAuthRetry?: boolean }): Promise<void> {
      const securedApi = useSecuredApi()

      try {
        const response = options?.skipAuthRetry
          ? await securedApi<AuthAuthGetSingleUserResponse>('/auth/user', {
              _skipAuthRetry: true,
            })
          : await securedApi<AuthAuthGetSingleUserResponse>('/auth/user')

        // Update state with user information
        this.user = response.data
        this.isAuthenticated = true
      } catch (error) {
        // Clear state on 401 (unauthorized)
        const fetchError = error as FetchError
        if (fetchError?.status === 401 || fetchError?.statusCode === 401) {
          this.clearState()
        }
        throw error
      }
    },

    /**
     * Refresh the access token
     *
     * Calls the backend refresh endpoint to obtain a new access token.
     * Uses a single-flight pattern to prevent multiple simultaneous refresh requests
     * within the same tab. Uses localStorage flag to synchronize refresh across tabs.
     * Uses exponential backoff when polling for another tab's refresh to complete.
     * Respects network offline state - waits for online status before attempting refresh.
     *
     * @throws Error if refresh fails
     */
    async refresh(): Promise<void> {
      // Single-flight pattern: if a refresh is already in progress in this tab,
      // wait for it to complete instead of starting a new one
      if (refreshPromise) {
        return refreshPromise
      }

      // Check if the device is offline before attempting refresh
      if (
        import.meta.client &&
        typeof navigator !== 'undefined' &&
        !navigator.onLine
      ) {
        // Wait for the device to come back online before proceeding
        await new Promise<void>((resolve, reject) => {
          const maxWaitMs = 30000 // 30 second timeout for coming online
          const startTime = Date.now()

          const handleOnline = () => {
            window.removeEventListener('online', handleOnline)
            resolve()
          }

          window.addEventListener('online', handleOnline)

          // Check periodically in case we missed the online event or it's already online
          const checkInterval = setInterval(() => {
            if (navigator.onLine) {
              clearInterval(checkInterval)
              window.removeEventListener('online', handleOnline)
              resolve()
            } else if (Date.now() - startTime > maxWaitMs) {
              clearInterval(checkInterval)
              window.removeEventListener('online', handleOnline)
              reject(new Error('Network offline timeout'))
            }
          }, 1000)
        })
      }

      // Check if another tab is already refreshing
      if (import.meta.client && getRefreshingFlag()) {
        // Wait for the other tab to complete refresh
        // Poll localStorage until the refreshing flag is cleared
        // Use exponential backoff: start at 50ms, double each time, max 500ms
        await new Promise<void>((resolve, reject) => {
          const maxWaitMs = 10000 // 10 second timeout
          const startTime = Date.now()
          const initialDelayMs = 50
          const maxDelayMs = 500
          let currentDelayMs = initialDelayMs

          const poll = () => {
            // Also check if we're still online during polling
            if (
              import.meta.client &&
              typeof navigator !== 'undefined' &&
              !navigator.onLine
            ) {
              reject(new Error('Network offline during refresh wait'))
              return
            }

            if (!getRefreshingFlag()) {
              resolve()
            } else if (Date.now() - startTime > maxWaitMs) {
              reject(new Error('Token refresh timeout'))
            } else {
              // Schedule next poll with exponential backoff
              setTimeout(poll, currentDelayMs)
              // Double the delay for next iteration, capped at max
              currentDelayMs = Math.min(currentDelayMs * 2, maxDelayMs)
            }
          }

          // Start polling with initial delay (true exponential backoff from the start)
          setTimeout(poll, initialDelayMs)
        })
        return
      }

      // Set refreshing flags
      this.isRefreshing = true
      if (import.meta.client) {
        setRefreshingFlag(true)
      }

      const securedApi = useSecuredApi()

      // Create the refresh promise and store it for single-flight
      const doRefresh = async () => {
        try {
          // Call refresh endpoint
          const response = await securedApi<{ data: { access_token: string } }>(
            '/auth/refresh-token',
            {
              method: 'POST',
            }
          )

          // Decode the access token to get expiration
          const decoded = decodeAccessToken(response.data.access_token)

          if (!decoded) {
            throw new Error('Failed to decode access token')
          }

          // Store token expiration in state and localStorage
          const tokenExpiresAtMs = decoded.exp * 1000 // Convert to milliseconds
          this.tokenExpiresAt = tokenExpiresAtMs
          if (import.meta.client) {
            saveTokenExpiration(tokenExpiresAtMs)
          }
        } catch (error) {
          // Clear state on refresh failure
          this.clearState()
          throw error
        } finally {
          // Clear refreshing flags and module-level promise
          this.isRefreshing = false
          refreshPromise = null
          if (import.meta.client) {
            setRefreshingFlag(false)
          }
        }
      }

      refreshPromise = doRefresh()
      return refreshPromise
    },

    /**
     * Clear all authentication state
     *
     * Clears user data, authentication status, and localStorage
     */
    clearState(): void {
      this.user = null
      this.isAuthenticated = false
      this.tokenExpiresAt = null
      this.isRefreshing = false
      // Note: isSystemNavigation is NOT reset here - it needs to persist through navigation
      // It will be reset after navigation completes
      // Note: sessionRestored is NOT reset here - it tracks if restoration was attempted,
      // not if the user is authenticated

      // Clear the module-level refresh promise to prevent stale promises
      _resetRefreshPromise()

      // Clear localStorage
      if (import.meta.client) {
        clearAuthStorage()
      }
    },

    /**
     * Restore session from localStorage on page reload
     *
     * This action is called by the auth.init plugin on client-side startup.
     * It reads localStorage for token_expires_at and attempts to restore the session.
     *
     * Flow:
     * 1. Read token_expires_at from localStorage
     * 2. If no data exists, remain unauthenticated
     * 3. If token has expired or is expiring soon (within 5 minutes), proactively refresh it
     * 4. Fetch current user to verify session is valid
     * 5. On any failure, clear state and remain unauthenticated
     *
     * @returns Promise<boolean> - true if session was restored, false otherwise
     */
    async restoreSession(): Promise<boolean> {
      // Only run on client side
      if (!import.meta.client) {
        return false
      }

      try {
        // Read token expiration from localStorage
        const storedExpiresAt = getTokenExpiration()

        // No stored session data - remain unauthenticated
        if (!storedExpiresAt) {
          return false
        }

        // Restore tokenExpiresAt to state
        this.tokenExpiresAt = storedExpiresAt

        // Check if token has already expired or is expiring soon (within configured buffer)
        const now = Date.now()
        const isExpired = now >= storedExpiresAt
        const isExpiringSoon = storedExpiresAt - now <= getTokenRefreshBuffer()

        if (isExpired || isExpiringSoon) {
          // Token expired or expiring soon - proactively refresh
          try {
            await this.refresh()
          } catch (error) {
            // Refresh failed - log error, clear state and remain unauthenticated
            console.error('Session restoration: token refresh failed', error)
            this.clearState()
            return false
          }
        }

        // Attempt to fetch user to verify session
        // Skip auth retry/interceptor handling since restoreSession manages its own error recovery
        try {
          await this.fetchUser({ skipAuthRetry: true })
          return true
        } catch (error) {
          // User fetch failed - clear state and remain unauthenticated
          console.error('Session restoration: user fetch failed', error)
          this.clearState()
          return false
        }
      } finally {
        // Always mark session restoration as complete, regardless of outcome
        // This allows the middleware to proceed with routing decisions
        this.sessionRestored = true
      }
    },
  },
})
